import { Button, Dialog, Icon, Sheet, useIsMobile } from '@forecastify/ui';
import { useT } from '../../i18n';
import { usePageHeader } from '../page-header';
import { PeriodDetail, type PeriodDetailData, type PeriodDetailHandlers } from './period-detail';
import { contractLabel } from './contract-label';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C29 · The period detail in three frames. The task surface's shape.        │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Same three as the contract and the line item, and for the same reason: three details that open
 * differently teach three habits.
 *
 *  * **drawer** — a `Sheet` at `min(560px, 96vw)`, narrow layout;
 *  * **modal** — a bare `Dialog` at 1060px with its own 48px header, wide layout;
 *  * **full page** — a route, `max-w-[1160px]`, wide layout.
 *
 * ⚠️ **Unlike the line item, `full` is real from the start** — a period HAS an address, because it
 * has a start date (P2-14). What it does not have is an id, which is the same fact stated twice.
 */
export type PeriodMode = 'drawer' | 'modal' | 'full';

export interface PeriodSurfaceProps {
  readonly mode: PeriodMode;
  readonly open: boolean;
  readonly data: PeriodDetailData;
  readonly handlers: PeriodDetailHandlers;
  readonly onModeChange: (mode: PeriodMode) => void;
  readonly onClose: () => void;
}

export function PeriodSurface(props: PeriodSurfaceProps) {
  const mobile = useIsMobile();
  if (props.mode === 'full') return <FullPage {...props} />;
  /* ⚠️ 17 Oct 2026 · on a phone a modal is the full-screen drawer — the product owner. */
  if (props.mode === 'modal' && !mobile) return <Modal {...props} />;
  return <Drawer {...props} />;
}

function Drawer({ open, data, handlers, onModeChange, onClose }: PeriodSurfaceProps) {
  const t = useT();

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={`${contractLabel(data.contract)} · #${String(data.period.index)}`}
      width="min(560px, 96vw)"
      closeLabel={t('common.close')}
      /* 17 Oct 2026 · no mode buttons on a phone: the drawer is the only frame there. */
      actions={
        useIsMobile()
          ? []
          : [
              {
                label: t('task.mode.modal'),
                icon: 'maximize',
                onSelect: () => {
                  onModeChange('modal');
                },
              },
            ]
      }
    >
      <PeriodDetail data={data} handlers={handlers} wide={false} />
    </Sheet>
  );
}

function Modal({ open, data, handlers, onModeChange, onClose }: PeriodSurfaceProps) {
  const t = useT();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      bare
      /* ⚠️ Item 36 · the same width as the contract and line-item modals. A detail modal that is
         narrower than its siblings is a surface somebody has to re-learn. */
      width="min(1320px, 94vw)"
      closeLabel={t('common.close')}
    >
      <div className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
        <span className="text-[12.5px]">
          {data.contract.identifier} · #{data.period.index}
        </span>
        <span className="text-muted-foreground min-w-0 flex-1 truncate text-[12.5px]">
          {data.period.from} – {data.period.to}
        </span>

        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.drawer')}
          title={t('task.mode.drawer')}
          onClick={() => {
            onModeChange('drawer');
          }}
        >
          <Icon name="minimize" size={15} />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.full')}
          title={t('task.mode.full')}
          onClick={() => {
            onModeChange('full');
          }}
        >
          <Icon name="maximize" size={15} />
        </Button>
        <Button variant="ghost" size="icon-sm" aria-label={t('common.close')} onClick={onClose}>
          <Icon name="x" size={15} />
        </Button>
      </div>

      {/* ⚠️ A flex column the detail FILLS rather than the scroller — 14 Oct. The detail's header
          stays put and its two halves scroll apart (`DetailFrame` in `detail-chrome.tsx`); the
          overflow is only the fallback for a body that is not bounded. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <PeriodDetail data={data} handlers={handlers} wide />
      </div>
    </Dialog>
  );
}

function FullPage({ data, handlers, onModeChange, onClose }: PeriodSurfaceProps) {
  const t = useT();

  usePageHeader(
    () => (
      <>
        <Button
          variant="ghost"
          size="icon-sm"
          className="-ml-1.5"
          aria-label={t('task.action.back')}
          onClick={onClose}
        >
          <Icon name="chevron-left" size={16} />
        </Button>
        <span className="text-[12.5px]">
          {data.contract.identifier} · #{data.period.index}
        </span>
        <span className="text-muted-foreground min-w-0 truncate text-[13px]">
          {data.period.from} – {data.period.to}
        </span>

        <div className="flex-1" />

        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.drawer')}
          title={t('task.mode.drawer')}
          onClick={() => {
            onModeChange('drawer');
          }}
        >
          <Icon name="minimize" size={15} />
        </Button>
      </>
    ),
    /* ⚠️⚠️ **VALUES, NOT CALLBACKS** — `hard-won.md` C24-1 records the 2,056-render loop that
       listing `onClose` and `onModeChange` here caused, and its symptom: the address changed and
       the previous screen stayed on it. */
    [data.contract.identifier, data.period.index, data.period.from, data.period.to],
  );

  return (
    /* ⚠️ No `data-screen` here — the ROUTE carries it, so the page is marked even when the period
       cannot be found. See `contracts-route.tsx`. */
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="mx-auto flex min-h-0 w-full max-w-[1160px] flex-1 flex-col">
        <PeriodDetail data={data} handlers={handlers} wide />
      </div>
    </div>
  );
}
